import React from 'react';
import { Award, BookOpen, CreditCard, Users } from 'lucide-react';

const AdditionalCostsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  licenseTypes 
}) => {
  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <CreditCard className="w-5 h-5 mr-2 text-purple-500" />
        Дополнительные расходы
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Включение учета лицензий */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeLicenseCosts"
              checked={formData.includeLicenseCosts}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <Award className="w-4 h-4 mr-1 text-purple-500" />
              Учитывать расходы на лицензии
            </span>
          </label>
        </div>

        {formData.includeLicenseCosts && (
          <div className="pl-7 border-l-2 border-purple-200 dark:border-purple-800 space-y-4">
            {/* Тип лицензирования */}
            <div>
              <label htmlFor="licenseType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Тип лицензирования
              </label>
              <select
                id="licenseType"
                name="licenseType"
                value={formData.licenseType}
                onChange={handleInputChange}
                className={`form-select w-full ${errors.licenseType ? 'input-error' : ''}`}
              >
                {licenseTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.licenseType && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.licenseType}
                </p>
              )}
              {formData.licenseType && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {licenseTypes.find(l => l.id === formData.licenseType)?.description || ''}
                </p>
              )}
            </div>

            {/* Количество лицензий */}
            <div>
              <label htmlFor="licenseCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {formData.licenseType === 'per_user' 
                  ? 'Количество пользователей' 
                  : formData.licenseType === 'per_core' 
                  ? 'Количество ядер' 
                  : formData.licenseType === 'per_cluster' 
                  ? 'Количество кластеров' 
                  : 'Количество подписок'}
              </label>
              <input
                type="text"
                id="licenseCount"
                name="licenseCount"
                value={formData.licenseCount}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.licenseCount ? 'input-error' : ''}`}
                placeholder="Введите количество"
                disabled={formData.licenseType === 'per_cluster'}
              />
              {errors.licenseCount && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.licenseCount}
                </p>
              )}
              {formData.licenseType === 'per_cluster' && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Лицензия на кластер покрывает все вычислительные ресурсы в кластере.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Включение расходов на обучение */}
        <div className="border-t border-purple-200 dark:border-purple-800 pt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeTrainingCosts"
              checked={formData.includeTrainingCosts}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <BookOpen className="w-4 h-4 mr-1 text-purple-500" />
              Учитывать расходы на обучение персонала
            </span>
          </label>
        </div>

        {formData.includeTrainingCosts && (
          <div className="pl-7 border-l-2 border-purple-200 dark:border-purple-800">
            <label htmlFor="trainingPeopleCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество обучаемых сотрудников
            </label>
            <input
              type="text"
              id="trainingPeopleCount"
              name="trainingPeopleCount"
              value={formData.trainingPeopleCount}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.trainingPeopleCount ? 'input-error' : ''}`}
              placeholder="Введите количество людей"
            />
            {errors.trainingPeopleCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.trainingPeopleCount}
              </p>
            )}
          </div>
        )}

        {/* Включение консалтинга */}
        <div className="border-t border-purple-200 dark:border-purple-800 pt-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeConsultingCosts"
              checked={formData.includeConsultingCosts}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <Users className="w-4 h-4 mr-1 text-purple-500" />
              Учитывать расходы на консалтинг
            </span>
          </label>
        </div>

        {formData.includeConsultingCosts && (
          <div className="pl-7 border-l-2 border-purple-200 dark:border-purple-800">
            <label htmlFor="consultingDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество дней консалтинга
            </label>
            <input
              type="text"
              id="consultingDays"
              name="consultingDays"
              value={formData.consultingDays}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.consultingDays ? 'input-error' : ''}`}
              placeholder="Введите количество дней"
            />
            {errors.consultingDays && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.consultingDays}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Консалтинг помогает оптимизировать процессы и избежать типичных ошибок при работе с Big Data
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
        <div className="flex items-start">
          <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
              Рекомендации по оптимизации затрат
            </h4>
            <p className="text-xs text-purple-600 dark:text-purple-400">
              Дополнительные затраты часто можно оптимизировать. Например, обучение может проводиться в групповом формате, 
              а лицензии можно приобретать по корпоративной программе со скидкой. 
              Для крупных проектов консалтинг в начале может значительно сэкономить ресурсы в будущем.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalCostsSection;